import type { ChannelItem, InsertionPolicy, ScopedLayer } from '@/types/api'

/**
 * Client-side mirror of the CHANNELS.md §4 insertion policies. The server's
 * resolver is authoritative (and backs "Preview for display…"); these helpers
 * only drive the explanatory layering visual and policy illustrations.
 */

export interface BlockPolicy {
  insertion: InsertionPolicy
  /** n for interleave(n), 1..20 — ignored for append / prepend. */
  every?: number | null
}

export const INTERLEAVE_MIN = 1
export const INTERLEAVE_MAX = 20

/** After every n-th element of `base` insert the next element of `layer`; leftovers are appended; `layer` is not cycled. */
export function interleave<T>(base: readonly T[], layer: readonly T[], every: number): T[] {
  if (base.length === 0) return [...layer]
  const n = Math.max(1, Math.floor(every) || 1)
  const out: T[] = []
  let next = 0
  base.forEach((element, index) => {
    out.push(element)
    if ((index + 1) % n === 0 && next < layer.length) out.push(layer[next++] as T)
  })
  return [...out, ...layer.slice(next)]
}

/** Merge one block (`layer`) into the list built so far (`base`) — CHANNELS.md §4 table. */
export function mergeBlock<T>(base: readonly T[], layer: readonly T[], policy: BlockPolicy): T[] {
  switch (policy.insertion) {
    case 'prepend':
      return [...layer, ...base]
    case 'interleave':
      return interleave(base, layer, policy.every ?? 1)
    default:
      return [...base, ...layer]
  }
}

export const LAYER_ORDER: readonly ScopedLayer[] = ['location', 'group', 'display']

export interface LayerBlock<T> {
  layer: ScopedLayer
  scopeId: string
  policy: BlockPolicy
  items: readonly T[]
}

/**
 * Applies blocks in fixed layer order (location → group → display) and, within a layer,
 * ascending scope id (ULID = creation order) — exactly like the server resolver.
 */
export function applyBlocks<T>(base: readonly T[], blocks: readonly LayerBlock<T>[]): T[] {
  const sorted = [...blocks].sort((a, b) => LAYER_ORDER.indexOf(a.layer) - LAYER_ORDER.indexOf(b.layer) || (a.scopeId < b.scopeId ? -1 : a.scopeId > b.scopeId ? 1 : 0))
  return sorted.reduce<T[]>((list, block) => mergeBlock(list, block.items, block.policy), [...base])
}

/** Block policy as stored on the block's first item (lowest position). */
export function blockPolicy(items: readonly Pick<ChannelItem, 'insertion' | 'interleave_every' | 'position'>[]): BlockPolicy {
  const first = [...items].sort((a, b) => a.position - b.position)[0]
  return { insertion: first?.insertion ?? 'append', every: first?.interleave_every ?? null }
}

export function policyLabel(policy: BlockPolicy): string {
  if (policy.insertion === 'interleave') return `Interleave every ${policy.every ?? 1}`
  return policy.insertion === 'prepend' ? 'Prepend' : 'Append'
}

export function policyDescription(policy: BlockPolicy): string {
  switch (policy.insertion) {
    case 'prepend':
      return 'These items play before the inherited list.'
    case 'interleave': {
      const n = policy.every ?? 1
      return `After every ${n === 1 ? '' : `${n}${ordinalSuffix(n)} `}inherited item, the next of these items is inserted. Leftovers play at the end.`
    }
    default:
      return 'These items play after the inherited list.'
  }
}

function ordinalSuffix(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return 'th'
  return ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th'
}
